import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    if (supported) {
      // Get current permission status
      setPermission(Notification.permission);
      
      // Check for existing subscription
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      
      // Check database for subscription state
      if (user && existingSubscription) {
        const { data: dbSubscriptions } = await supabase
          .from('push_subscriptions')
          .select('*')
          .eq('user_id', user.id);
        
        if (dbSubscriptions?.length) {
          setIsSubscribed(true);
        }
      }
      
      setSubscription(existingSubscription);
      setIsSubscribed(!!existingSubscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const requestPermission = async (): Promise<NotificationPermission> => {
    const permission = await Notification.requestPermission();
    setPermission(permission);
    return permission;
  };

  const subscribe = async () => {
    if (!isSupported || !user) return;

    try {
      // Request permission if needed
      if (permission === 'default') {
        const newPermission = await requestPermission();
        if (newPermission !== 'granted') return;
      }

      if (permission !== 'granted') return;

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Use your actual VAPID public key here
      const vapidPublicKey = 'BEaKHvA1fAA1kbbHtupltsU65YNMNtGrEX4l2iLVLLVHmBiNRrLJzcdHFdSvS_gFYskJB3hsq5uBPQt1ikeYh8Q';

      // Subscribe to push notifications
      const convertedKey = urlBase64ToUint8Array(vapidPublicKey);
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey as unknown as BufferSource
      });

      // Store subscription in database
      const { error } = await supabase
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          subscription: pushSubscription.toJSON() as any
        });

      if (error) {
        console.error('Error storing subscription:', error);
        throw error;
      }

      setSubscription(pushSubscription);
      setIsSubscribed(true);
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
    }
  };

  const unsubscribe = async () => {
    if (!subscription || !user) return;

    try {
      // Unsubscribe from push notifications
      await subscription.unsubscribe();

      // Remove from database
      const { error } = await supabase
        .from('push_subscriptions')
        .delete()
        .eq('user_id', user.id);

      if (error) {
        console.error('Error removing subscription from database:', error);
      }

      setSubscription(null);
      setIsSubscribed(false);
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
    }
  };

  return {
    isSupported,
    isSubscribed,
    permission,
    subscribe,
    unsubscribe,
    requestPermission
  };
}

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}